import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import CustomPlanBanner from "@/components/CustomPlanBanner";

const PLANS = [
  {
    slug: "monthly",
    name: "Monthly",
    price: "$25",
    cadence: "/ month",
    blurb: "Great for light users.",
    features: [
      "1 cleaning a month, up to 2 bins",
      "$8 per additional bin",
      "Eco-safe soap",
      "Sanitizing rinse",
      "Deodorizer finish",
      "Photo proof + email receipt",
    ],
    highlight: false,
    cta: "Choose Monthly",
  },
  {
    slug: "biweekly",
    name: "Bi-Weekly",
    price: "$40",
    cadence: "/ month",
    blurb: "Includes 2 bins · 2 cleanings per month",
    features: [
      "2 cleanings a month, up to 2 bins",
      "$8 per additional bin",
      "Eco-safe soap, sanitizing rinse, deodorizer",
      "Photo proof on every visit",
      "Email updates after each cleaning",
      "Cancel anytime",
    ],
    highlight: true,
    cta: "Choose Bi-Weekly",
  },
  {
    slug: "onetime",
    name: "One-Time",
    price: "$40",
    cadence: "/ bin",
    blurb: "Try us before subscribing.",
    features: [
      "Single deep clean",
      "$8 per additional bin",
      "Eco-safe soap",
      "Sanitizing rinse",
      "Deodorizer finish",
      "Photo proof + email receipt",
    ],
    highlight: false,
    cta: "Choose One-Time",
  },
];

const COMPARE = [
  "Eco-safe biodegradable soap",
  "Sanitizing high-pressure rinse",
  "Deodorizer finishing spray",
  "Photo proof every clean",
  "Email updates & receipts",
];

export default function Plans() {
  return (
    <div data-testid="page-plans" className="bg-white">
      <section className="relative bg-gradient-to-b from-brand-50 to-white">
        <div className="container-page relative pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="max-w-3xl">
            <p className="overline mb-4">Plans &amp; Pricing</p>
            <h1 className="font-heading h1-fluid font-extrabold text-ink text-balance">
              Pick a plan. <span className="text-brand">Forget the smell.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Clear prices. No contracts. Cancel anytime. Switch plans anytime.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="pricing-section">
        <div className="container-page">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {PLANS.map((p, idx) => (
              <div
                key={p.slug}
                data-reveal
                style={{ "--reveal-delay": `${idx * 80}ms` }}
                className={`relative rounded-3xl bg-white p-7 sm:p-8 lg:p-10 flex flex-col transition-all duration-300 border border-slate-100 border-l-[6px] ${
                  p.highlight
                    ? "border-l-gold shadow-lift lg:-translate-y-3"
                    : "border-l-transparent shadow-soft hover:-translate-y-1 hover:shadow-lift"
                }`}
                data-testid={`plan-card-${p.slug}`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-accent text-white text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full shadow-soft">
                    <Sparkles size={11} /> Most Popular
                  </span>
                )}
                <h3 className="font-heading text-3xl font-bold text-ink mb-2">
                  {p.name}
                </h3>
                <p className="text-sm text-slate-500 mb-7">{p.blurb}</p>

                <div className="flex items-baseline gap-1 mb-7">
                  <span className="font-heading text-5xl sm:text-6xl font-extrabold text-ink leading-none">
                    {p.price}
                  </span>
                  <span className="text-sm text-slate-500 ml-1">
                    {p.cadence}
                  </span>
                </div>

                <ul className="space-y-3 mb-9 flex-1">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 text-sm text-slate-700"
                    >
                      <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                        <Check size={12} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/signup?plan=${p.slug}`}
                  className={`${p.highlight ? "btn-primary" : "btn-secondary"} w-full whitespace-nowrap`}
                  data-testid={`plan-cta-${p.slug}`}
                >
                  {p.cta}
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>

          <CustomPlanBanner />

          <div className="mt-20 lg:mt-24">
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <p className="overline mb-3">Every plan includes</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink text-balance">
                  The full Silky Smooth treatment ,{" "}
                  <span className="text-brand">no upsells.</span>
                </h2>
              </div>
              <div className="lg:col-span-7">
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                  {COMPARE.map((c) => (
                    <li
                      key={c}
                      className="flex items-start gap-3 text-base text-slate-700"
                    >
                      <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                        <Check size={12} />
                      </span>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-50/40 py-16">
        <div className="container-page flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl sm:text-3xl font-bold text-ink">
              More than 2 bins, or commercial service?
            </h3>
            <p className="text-slate-600 mt-2">
              We do custom quotes for HOAs and businesses too.
            </p>
          </div>
          <Link to="/contact" className="btn-secondary" data-testid="plans-contact-cta">
            Talk to Isaac <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
